import { connectorTypeOf, connectorTypes } from "../../../shared/connectors";
import type { AppTag } from "../../../shared/connectors/tags";
import type { ConnectorDefinition } from "../../../shared/connectors/types";
import { config } from "../../environment";
import { TOKEN_MEASURES } from "../connectorUsage";

export type ConnectorConfig = {
  name: string;
  identifier: string;
  tagLine: string;
  tags: AppTag[];
  definition: ConnectorDefinition;
};

const isAvailable = (definition: ConnectorDefinition) => {
  if (definition.oauth?.clientCredentialsFrom === "env") {
    return config.hasOAuthClients;
  }
  if (definition.oauth?.clientCredentialsFrom === "managed") {
    return !!config.managedOAuthClientIds[definition.identifier];
  }
  return true;
};

const toConfig = (definition: ConnectorDefinition): ConnectorConfig => ({
  name: definition.name,
  identifier: definition.identifier,
  tagLine: definition.tagLine,
  tags: definition.tags,
  definition,
});

const availableVariants = new Map<string, ConnectorConfig[]>();
for (const type of connectorTypes) {
  const variants = type.variants.filter(isAvailable).map(toConfig);
  if (variants.length > 0) availableVariants.set(type.identifier, variants);
}

const connectorNameCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

export const connectorDirectory: ConnectorConfig[] = [
  ...availableVariants.values(),
]
  .map((variants) => variants[0]!)
  .sort((a, b) => connectorNameCollator.compare(a.name, b.name));

// The custom connector types worth surfacing by default in the quick-create
// pickers, most-reached-for first. The niche remainder (login credentials,
// SMTP, LDAP) stays creatable from the connectors directory’s “Custom”
// category rather than crowding these menus.
const recommendedCustomConnectorIds = [
  "apiKey",
  "customOauth",
  "remoteMcp",
  "workflow",
];

export const recommendedCustomConnectors: ConnectorConfig[] =
  recommendedCustomConnectorIds
    .map((id) => connectorDirectory.find((app) => app.identifier === id))
    .filter((app): app is ConnectorConfig => app !== undefined);

export function connectorVariants(config: ConnectorConfig): ConnectorConfig[] {
  const type = connectorTypeOf(config.identifier);
  return (type && availableVariants.get(type.identifier)) ?? [];
}

const allConfigs = [...availableVariants.values()].flat();

export function resolveConnectorConfig(
  identifier: string | undefined | null
): ConnectorConfig | undefined {
  if (!identifier) return undefined;
  const exact = allConfigs.find((app) => app.identifier === identifier);
  if (exact) return exact;
  return allConfigs.find(
    (app) =>
      identifier.endsWith(`-${app.identifier}`) ||
      identifier.endsWith(`_${app.identifier}`)
  );
}

export function connectorProducesTokens(
  identifier: string | undefined | null
): boolean {
  const usage = resolveConnectorConfig(identifier)?.definition.usage;
  return usage?.measures.some((m) => TOKEN_MEASURES.has(m.measure)) ?? false;
}
